import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MachineType } from '../types';
import { machineTypeApi } from '../api/machines';
import { Toast, useToast } from '../components/Toast';

const MachineTypes: React.FC = () => {
  const [types, setTypes] = useState<MachineType[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<MachineType | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const { toast, showToast, dismissToast } = useToast();

  const load = useCallback(async () => {
    try {
      const { data } = await machineTypeApi.getAll();
      setTypes(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setError('');
    try {
      await machineTypeApi.create({ name: newName.trim() });
      setNewName('');
      load();
      showToast('success', 'Typ maszyny dodany.');
    } catch {
      setError('Nie udało się dodać typu');
      showToast('error', 'Nie udało się dodać typu.');
    }
  };

  const handleUpdate = async (id: string) => {
    if (!editName.trim()) return;
    try {
      await machineTypeApi.update(id, { name: editName.trim() });
      setEditId(null);
      load();
      showToast('success', 'Typ maszyny zaktualizowany.');
    } catch {
      setError('Nie udało się zaktualizować typu');
      showToast('error', 'Nie udało się zaktualizować typu.');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setError('');
    try {
      await machineTypeApi.delete(deleteTarget.id);
      setDeleteTarget(null);
      load();
      showToast('success', 'Typ maszyny usunięty.');
    } catch {
      setError('Nie udało się usunąć typu');
      showToast('error', 'Nie udało się usunąć typu.');
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div>
      <Link to="/machines" className="btn btn-secondary btn-sm" style={{ marginBottom: '1rem' }}>
        &larr; Powrót
      </Link>
      <h1 className="page-title">Typy maszyn</h1>

      <form onSubmit={handleAdd} className="card" style={{ padding: '1rem', marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <input
          className="form-input"
          style={{ flex: 1, minWidth: 200 }}
          placeholder="Nazwa nowego typu..."
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          required
        />
        <button className="btn btn-primary" type="submit">Dodaj</button>
      </form>

      {error && <div className="auth-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      {loading ? (
        <div className="empty-state">Ładowanie...</div>
      ) : types.length === 0 ? (
        <div className="empty-state">Brak typów maszyn</div>
      ) : (
        <div className="items-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {types.map((t) => (
            <div key={t.id} className="card" style={{ padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              {editId === t.id ? (
                <>
                  <input
                    className="form-input"
                    style={{ flex: 1, minWidth: 150 }}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    autoFocus
                  />
                  <button className="btn btn-primary btn-sm" onClick={() => handleUpdate(t.id)}>Zapisz</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditId(null)}>Anuluj</button>
                </>
              ) : (
                <>
                  <span style={{ flex: 1, fontWeight: 500 }}>{t.name}</span>
                  <button className="btn btn-primary btn-sm" onClick={() => { setEditId(t.id); setEditName(t.name); }}>Edytuj</button>
                  <button className="btn btn-danger btn-sm" onClick={() => { setError(''); setDeleteTarget(t); }}>Usuń</button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {deleteTarget && (
        <div className="modal-overlay" onClick={() => { if (!deleteBusy) setDeleteTarget(null); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <h2 className="modal-title">Usuń typ maszyny</h2>
            <p style={{ marginBottom: '0.75rem' }}>
              Czy na pewno chcesz usunąć typ <strong>{deleteTarget.name}</strong>?
            </p>
            <p className="item-meta" style={{ marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Tej operacji nie można cofnąć.
            </p>
            {error && <div className="auth-error">{error}</div>}
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>
                Anuluj
              </button>
              <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={deleteBusy}>
                {deleteBusy ? 'Usuwam…' : 'Usuń'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

export default MachineTypes;
